'use client';

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface SetupStoreFormRowProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  optionalLabel?: string;
  hint?: string;
  error?: string;
  /** Leading icon rendered in the soft brand-tinted tile. */
  icon: LucideIcon;
  children: ReactNode;
}

/** Horizontal setup-store field row: brand icon tile · label + hint · control on the right. */
export function SetupStoreFormRow({
  label,
  htmlFor,
  required,
  optionalLabel,
  hint,
  error,
  icon: Icon,
  children,
}: SetupStoreFormRowProps) {
  const errorId = error && htmlFor ? `${htmlFor}-error` : undefined;

  return (
    <div className="flex flex-col gap-4 py-5 first:pt-1 last:pb-1 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="flex items-center gap-3.5">
        <span
          className="text-primary flex size-9 shrink-0 items-center justify-center rounded-[0.625rem] bg-[color-mix(in_srgb,var(--primary)_13%,var(--card))]"
          aria-hidden
        >
          <Icon className="size-[1.125rem]" strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <label
            htmlFor={htmlFor}
            className="text-foreground inline-flex items-baseline gap-1.5 text-[0.9375rem] font-semibold leading-tight"
          >
            {label}
            {required ? (
              <span className="text-destructive" aria-hidden>
                *
              </span>
            ) : null}
            {optionalLabel ? (
              <span className="text-muted-foreground text-xs font-normal">{optionalLabel}</span>
            ) : null}
          </label>
          {hint ? (
            <p className="text-muted-foreground m-0 mt-0.5 text-[0.8125rem] leading-[1.45]">{hint}</p>
          ) : null}
        </div>
      </div>

      <div className="w-full min-w-0 sm:w-72 sm:shrink-0">
        <div className="w-full sm:flex sm:justify-end">{children}</div>
        {error ? (
          <p id={errorId} className="text-destructive m-0 mt-2 text-xs sm:text-right" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
